import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';

import Chart from '../../chart';
import VolSeriesChip from '../../../series-chip/vol_series_chip';
import { ReportFilterMultiSelect } from '../../../report-filter-buttons/report-filter-multi-select';

import {
  DecayWidgetContext,
  SPOT_LIST,
} from '../../../../providers/decay-widget/decay-widget-provider';
import Utils from '../../../../utils/immersive_utils';
import chartTheme from '../../chart/theme';

import './styles.scss';

type DecayContentProps = {
  dataType: 'deltaDecay' | 'thetaDecay';
  currency: string;
};

type ShockChip = {
  volShock: number;
  label: string;
  colors: string[];
  visible: boolean;
};

const colorPaletteTriplets: string[][] = [
  ['#93ECEC', '#26D9D9', '#178282'],
  ['#77BCEE', '#0095FF', '#145BF5'],
  ['#806CE5', '#5A40DD', '#311C9C'],
  ['#B267E4', '#9126D9', '#4B0977'],
  ['#A844AB', '#79327C', '#3D193E'],
  ['#FF66AB', '#FF338F', '#CC005C'],
  ['#F589D5', '#A50D78', '#7B0A59'],
  ['#F6956F', '#F3733F', '#F04F0F'],
  ['#FCCE92', '#FAA638', '#DF8206'],
  ['#FBD26A', '#FFBE1A', '#805B00'],
  ['#CFA67C', '#B2804D', '#8F663D'],
  ['#ABBD75', '#889F5C', '#5A6732'],
];

const DecayContent = ({ dataType, currency }: DecayContentProps) => {
  const { volShockList, isLoading, delta, theta, setIsVisible, selectedSpots, setSelectedSpots } =
    useContext(DecayWidgetContext);
  const [chips, setChips] = useState<ShockChip[]>([]);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    const next: ShockChip[] = (volShockList || []).map((v: any, idx: number) => ({
      volShock: parseFloat(v.value),
      label: `${parseFloat(v.value) * 100}%`,
      colors: colorPaletteTriplets[idx % colorPaletteTriplets.length],
      visible: true,
    }));
    setChips(next);
  }, [volShockList]);

  const data = dataType === 'deltaDecay' ? delta : theta;

  const datasetForCurrency = useMemo(() => {
    if (!data || !currency) return null;
    return data[currency] || null;
  }, [data, currency]);

  const horizons: string[] = useMemo(() => {
    if (!datasetForCurrency) return [];
    const set = new Set<string>();
    Object.values(datasetForCurrency).forEach((items: any) => {
      (items as any[]).forEach((it) => set.add(String(it.horizon_date)));
    });
    const arr = Array.from(set);
    arr.sort((a, b) => parseInt(a) - parseInt(b));
    return arr.map((d) => d.replace(/^(\d+)D$/, 'T+$1'));
  }, [datasetForCurrency]);

  const visibleChips = chips.filter((c) => c.visible);

  const series = useMemo(() => {
    if (!datasetForCurrency || horizons.length === 0) return [] as any[];

    const out: any[] = [];
    visibleChips.forEach((c, chipIdx) => {
      const key = String(c.volShock);
      const items: any[] = (datasetForCurrency as any)[key] || [];

      const byHSpot = new Map<string, Map<string, number>>();
      items.forEach((it) => {
        const h = String(it.horizon_date);
        const spotMap = new Map<string, number>();
        (it.value || []).forEach((sv: any) => {
          spotMap.set(String(sv.spot), Number(sv.value));
        });
        byHSpot.set(h, spotMap);
      });

      selectedSpots.forEach((spotVal, spotIdx) => {
        const spotIndex = Math.max(0, SPOT_LIST.indexOf(String(spotVal)));
        const values = horizons.map((hT) => {
          const h = hT.replace(/^T\+(\d+)$/, '$1D');
          const spotMap = byHSpot.get(h);
          const v = spotMap ? spotMap.get(String(spotVal)) : undefined;
          return v == null ? 0 : v;
        });

        out.push({
          name: `Vol ${c.label} • Spot ${parseFloat(String(spotVal)) * 100}%`,
          type: 'bar',
          stack: `vol-${chipIdx}-${c.volShock}`,
          data: values,
          itemStyle: { color: c.colors[spotIndex] || c.colors[spotIdx] || c.colors[0] },
          barMaxWidth: 10,
        });
      });
    });

    return out;
  }, [datasetForCurrency, horizons, visibleChips, selectedSpots]);

  const xAxis = useMemo(
    () => ({
      type: 'category',
      data: horizons,
      boundaryGap: true,
      axisTick: { alignWithLabel: true, interval: 0 },
      splitLine: {
        show: true,
        interval: 0,
        lineStyle: { color: (chartTheme as any)?.colors?.splitLine },
      },
      axisLabel: {
        interval: 'auto' as any,
        hideOverlap: true,
        rotate: 0,
      },
    }),
    [horizons],
  );

  const yAxis = useMemo(
    () => ({
      type: 'value',
      position: 'right',
      axisLabel: { formatter: (v: any) => Utils.formatNumber(Number(v)) },
    }),
    [],
  );

  const handleChipClick = useCallback((volShock: number) => {
    setChips((prev) => {
      const next = prev.map((c) => (c.volShock === volShock ? { ...c, visible: !c.visible } : c));
      if (next.every((c) => !c.visible)) return prev;
      return next;
    });
  }, []);

  const resetChips = useCallback(() => {
    setChips((prev) => prev.map((c) => ({ ...c, visible: true })));
  }, []);

  return (
    <div className="decay-content">
      <div className="decay-spot-filters">
        <ReportFilterMultiSelect
          titleFilter="Spot"
          dataArr={[
            { isVisible: true, value: '0.1', label: '+10%' },
            { isVisible: true, value: '0', label: '0%' },
            { isVisible: true, value: '-0.1', label: '-10%' },
          ]}
          selected={selectedSpots}
          setSelected={setSelectedSpots}
          disableBorder
          className="decay-spot-select"
        />
      </div>
      <div className="risk-graph-chips-container">
        <VolSeriesChip
          series={{ label: 'All', color: 'transparent' } as any}
          onClick={resetChips}
          classnames="decay-all-chip"
          disabled={visibleChips.length === chips.length}
        />
        <div className="chips-divider" />
        {chips.map((c) => (
          <div key={c.volShock} className="risk-graph-chips">
            <VolSeriesChip
              series={{ label: c.label, color: c.colors[1] } as any}
              onClick={() => handleChipClick(c.volShock)}
              removable={false}
              disabled={!c.visible}
            />
          </div>
        ))}
      </div>

      <Chart
        className="decay-chart"
        series={series}
        xAxis={xAxis as any}
        yAxis={yAxis as any}
        merge={false}
        isLoading={isLoading}
        grid={{ left: 10, right: 0, top: 5, bottom: 0 }}
        legend={{ show: false }}
      />

      <div className="decay-footer">
        {visibleChips.map((c) =>
          selectedSpots.map((spotVal, spotIdx) => {
            const val = parseFloat(String(spotVal)) * 100;
            const spotLabel = `${val > 0 ? '+' : ''}${val}%`;
            const colori = Math.max(0, SPOT_LIST.indexOf(String(spotVal)));
            const color = c.colors[colori] || c.colors[spotIdx] || c.colors[0];
            return (
              <div key={`f-${c.volShock}-${spotVal}`} className="footer-item">
                <span className="color-indicator" style={{ backgroundColor: color }} />
                <span className="footer-text">{`Spot ${spotLabel} • Vol shock ${c.label}`}</span>
              </div>
            );
          }),
        )}
      </div>
    </div>
  );
};

export default DecayContent;
